import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import {
  alpha,
  Box,
  ButtonBase,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router';
import { NAV } from './nav';

/**
 * The app's history, one entry per history index (react-router keeps `idx` in history.state), with
 * the title each page showed. Kept per tab in sessionStorage, so it survives a reload.
 */
const STORE = 'pp-back-titles';
type Titles = Record<number, string>;

function readTitles(): Titles {
  try {
    return JSON.parse(sessionStorage.getItem(STORE) ?? '{}') as Titles;
  } catch {
    return {};
  }
}

function writeTitle(idx: number, title: string) {
  try {
    const titles = readTitles();
    if (titles[idx] === title) return;
    sessionStorage.setItem(STORE, JSON.stringify({ ...titles, [idx]: title }));
  } catch {
    // Storage blocked: the button falls back to the section name.
  }
}

const historyIdx = () => {
  const state = window.history.state as { idx?: number } | null;
  return typeof state?.idx === 'number' ? state.idx : 0;
};

/** The sidebar item whose screen this exactly is ("Dashboard", "Customers"), if any. */
const navScreen = (path: string) =>
  NAV.flatMap((g) => g.items).find((item) => item.path === path)?.label ?? null;

/** The sidebar section a path belongs to ("Customers" for /contacts/…), as a fallback title. */
function sectionOf(path: string): string | null {
  let best: { label: string; length: number } | null = null;
  for (const item of NAV.flatMap((g) => g.items)) {
    const match = item.path === '/' ? path === '/' : path.startsWith(item.path);
    if (match && (!best || item.path.length > best.length))
      best = { label: item.label, length: item.path.length };
  }
  return best?.label ?? null;
}

/** The page's own title: the first heading in the main area. */
const pageHeading = () =>
  document.querySelector('main h1, main h2, main h3')?.textContent.trim().slice(0, 80) ?? '';

/** The page's selected tab ("Ledger"), so a tab switch reads "Back to Sana Interiors · Ledger". */
const selectedTab = () =>
  document.querySelector('main [role="tab"][aria-selected="true"]')?.textContent.trim() ?? '';

/** An open dialog, drawer or menu (MUI modals render outside main). */
const openModal = () => document.querySelector('.MuiModal-root:not(.MuiModal-hidden)');

/**
 * Back to the previous screen of the app (like the browser's Back), shown in the top bar whenever
 * there is one. It names where it goes. In the desktop app, which has no browser buttons, Alt+← and
 * the mouse's back button do the same.
 */
export function BackButton({
  compactBelow = 'sm',
}: {
  /** Below this width only the arrow shows (the POS bar is busier than the app's). */
  compactBelow?: 'sm' | 'md' | 'lg';
} = {}) {
  const compact = useMediaQuery(useTheme().breakpoints.down(compactBelow));
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  // react-router updates history.state before it renders the new location, so this is current.
  const idx = historyIdx();

  // Track where we are, and remember this page's title as it appears (it may arrive with the data).
  useEffect(() => {
    const here = historyIdx();
    const remember = () => {
      // A sidebar screen goes by its sidebar name (the dashboard's heading is a greeting); deeper
      // screens by their own heading ("Sana Interiors"), else by their section.
      const title = navScreen(location.pathname) || pageHeading() || sectionOf(location.pathname);
      // Name the tab only when one was picked (?tab=); the default tab is just the page.
      const tab = new URLSearchParams(location.search).has('tab') ? selectedTab() : '';
      if (title) writeTitle(here, tab && tab !== title ? `${title} · ${tab}` : title);
    };
    remember();
    const main = document.querySelector('main');
    if (!main) return;
    // At once, not debounced: a page that keeps loading (linked documents, totals) would otherwise
    // be left before its title was saved. Once history has moved on, the DOM is the next page's.
    const observer = new MutationObserver(() => {
      if (historyIdx() === here) remember();
    });
    observer.observe(main, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [location.key, location.pathname, location.search, navigationType]);

  const canGoBack = idx > 0;

  // Desktop app: Alt+← and the mouse back button (browsers already do this themselves). With a
  // dialog open they close it first, as Esc does, rather than leaving the page behind it.
  useEffect(() => {
    if (!('packpilot' in window)) return;
    const back = () => {
      const modal = openModal();
      if (modal) {
        modal.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
      } else if (canGoBack) {
        void navigate(-1);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      }
    };
    const onMouse = (e: MouseEvent) => {
      if (e.button === 3) {
        e.preventDefault();
        back();
      }
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mouseup', onMouse);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mouseup', onMouse);
    };
  }, [canGoBack, navigate]);

  if (!canGoBack) return null;
  // A step that never rendered (two clicks within a frame) has no title: it was almost certainly
  // this same page, so name the page.
  const titles = readTitles();
  const target =
    titles[idx - 1] ??
    titles[idx]?.split(' · ')[0] ??
    (sectionOf(location.pathname) || 'previous page');

  return (
    <Tooltip title={`Back to ${target} (Alt+←)`}>
      <ButtonBase
        onClick={() => void navigate(-1)}
        aria-label={`Back to ${target}`}
        sx={(t) => {
          const accent = t.vars?.palette.primary.main ?? t.palette.primary.main;
          return {
            flexShrink: 0,
            height: 40,
            minWidth: 40,
            pl: compact ? 0 : 0.5,
            pr: compact ? 0 : 1.6,
            gap: 0.9,
            justifyContent: compact ? 'center' : 'flex-start',
            borderRadius: compact ? '50%' : '12px',
            bgcolor: 'background.paper',
            border: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
            boxShadow: '0 1px 2px rgba(16,24,40,0.05), inset 0 1px 0 rgba(255,255,255,0.6)',
            ...t.applyStyles('dark', { boxShadow: '0 1px 2px rgba(0,0,0,0.4)' }),
            transition: 'border-color 160ms, box-shadow 160ms, background-color 160ms',
            animation: 'pp-back-in 200ms cubic-bezier(0.2,0,0,1)',
            '@keyframes pp-back-in': {
              from: { opacity: 0, transform: 'scale(0.96)' },
              to: { opacity: 1, transform: 'none' },
            },
            '& .pp-back-arrow': { transition: 'transform 160ms cubic-bezier(0.2,0,0,1)' },
            '&:hover': {
              borderColor: alpha(t.palette.primary.main, 0.45),
              boxShadow: `0 6px 16px -8px ${alpha(t.palette.primary.main, 0.45)}`,
            },
            '&:hover .pp-back-arrow': { transform: 'translateX(-2px)' },
            '&:active .pp-back-arrow': { transform: 'translateX(-4px)' },
            '&.Mui-focusVisible': {
              outline: `2px solid ${alpha(t.palette.primary.main, 0.55)}`,
              outlineOffset: 2,
            },
            '& .pp-back-icon': {
              color: accent,
              background: `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.16)}, ${alpha(t.palette.primary.main, 0.08)})`,
            },
          };
        }}
      >
        <Box
          className="pp-back-icon"
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <ArrowBackRounded className="pp-back-arrow" sx={{ fontSize: 18 }} />
        </Box>
        {!compact && (
          <Typography
            component="span"
            sx={{ fontSize: 14, fontWeight: 650, color: 'text.primary', lineHeight: 1 }}
          >
            Back
          </Typography>
        )}
      </ButtonBase>
    </Tooltip>
  );
}
